import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMap } from './map-view';

/** A small label pinned to a map position (follows panning and zooming). */
export function MapTag({
  at,
  children,
  color = '#0f172a',
}: {
  at: [number, number];
  children: ReactNode;
  color?: string;
}) {
  const map = useMap();
  const [, redraw] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!map) return;
    const update = () => redraw((n) => n + 1);
    map.on('move', update);
    return () => {
      map.off('move', update);
    };
  }, [map]);
  // Measure the label once it is drawn so it can be kept inside the map.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the text changes
  useLayoutEffect(() => {
    if (map && ref.current) redraw((n) => n + 1);
  }, [map, children]);
  if (!map) return null;
  const p = map.project(at);
  // Keep the whole label inside the map when its point is near an edge.
  const half = (ref.current?.offsetWidth ?? 0) / 2;
  const width = map.getContainer().clientWidth;
  const left = half ? Math.min(Math.max(p.x, half + 4), width - half - 4) : p.x;
  return (
    <div
      ref={ref}
      className="pointer-events-none absolute z-10 rounded-md px-1.5 py-0.5 text-[11px] font-semibold whitespace-nowrap text-white shadow ring-1 ring-white"
      style={{ left, top: p.y, transform: 'translate(-50%, calc(-100% - 10px))', background: color }}
    >
      {children}
    </div>
  );
}
